'use client'

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CirclePlus, RefreshCw, Search, X } from 'lucide-react'

import type { SeatGroup } from '@package/drizzle/queries'
import { NO_SCHOOL_CLASS_ID } from '@package/api/schemas'
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../context'
import { SchoolClassSelect } from './school-class-select'
import { seatSourceLabel } from './seat-source'
import { StudentPickerList } from './student-picker-list'

export interface ManageSeatsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Which roster is being seated. Decides the noun, and whether classes are a thing at all. */
  type?: 'web' | 'school'
  /**
   * Sends the buyer to buy more seats, with the shortfall as `premium_seats`.
   *
   * A prop because the two apps route differently and this package has no router of its own —
   * the same reason `NewForm` takes `onNavigate`.
   */
  onBuySeats?: (shortfall: number) => void
}

/**
 * Names a block of seats by its entitlement flavour — only a sponsored scholarship reads "Beasiswa".
 *
 * An add-on block is named for what it is instead. It inherits its pool's flavour, so naming it the
 * same way would put two identically-labelled rows in the picker whose only difference is a date.
 */
function groupLabel(group: SeatGroup): string {
  return group.kind === 'add-on' ? 'Kursi tambahan' : seatSourceLabel(group.sourceType, group.isCommercial)
}

/**
 * The window a block of seats covers.
 *
 * A membership block gets ONE date, which is the one an admin needs: a scheduled pool is named by
 * when it OPENS (the students picked read as Terbatas until then, and an end date a year out gives
 * no way to tell why), a live one by when it ENDS.
 *
 * An add-on block gets BOTH, because it is a purchase rather than a term — bought on a day of its
 * own and running to the end of everything the school had already paid for. "Berakhir 1 Sep 2028"
 * alone would hide that these seats are usable today; "Mulai 18 Sep 2026" alone would hide how far
 * they reach. Both is the only honest label, and it is the one the checkout already quoted.
 */
function groupWindowLabel(group: SeatGroup): string {
  if (group.kind === 'add-on') {
    return `${formatDate(group.startDate, { dateStyle: 'medium' })} – ${formatDate(group.endDate, { dateStyle: 'medium' })}`
  }

  return group.isScheduled
    ? `Mulai ${formatDate(group.startDate, { dateStyle: 'medium' })}`
    : `Berakhir ${formatDate(group.endDate, { dateStyle: 'medium' })}`
}

/**
 * "Kelola kursi": spend the school's premium seats on students who have none.
 *
 * A school may hold several blocks of seats at once with different windows — 50 purchased seats to
 * December, a 20-seat scholarship to March, and an add-on bought last week that runs to both — so
 * the picker names the block being spent and the assignment is scoped to it server-side
 * (`giveChildrenPremium.membershipId` / `.addOnId`). With one block there is nothing to choose, so
 * the picker is hidden and that block is spent implicitly.
 *
 * The unit here is a `SeatGroup`, NOT a pool: a pool folds its add-ons into the membership they
 * ride, which put seats bought mid-term under the end date of next term's pool. See
 * `getOrgSeatGroups`.
 *
 * Only students holding no active seat at all are offered. `premiumId` alone is not enough: a child
 * reached by a parent's family subscription (`familyPremiumId`) already reads as Premium in the app,
 * and offering them a second seat is what made schools spend seats for nothing.
 */
export function ManageSeatsDialog({ open, onOpenChange, type = 'school', onBuySeats }: ManageSeatsDialogProps) {
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [groupKey, setGroupKey] = useState<string | null>(null)
  const [schoolClassId, setSchoolClassId] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const {
    data: groupsData,
    isPending: isPendingPools,
    isError: isErrorPools,
    refetch: refetchPools,
  } = useQuery({
    // Scheduled blocks included: a renewal buys seats that open next term, and a school planning
    // ahead assigns them from here. They are labelled as future in the picker below.
    ...trpc.myOrganization.getSeatGroupsWithScheduled.queryOptions(),
    enabled: open,
  })
  const groups = groupsData ?? []

  const {
    data: childrenData,
    isPending: isPendingChildren,
    isError: isErrorChildren,
    refetch: refetchChildren,
  } = useQuery({
    ...trpc.myOrganization.listChildren.queryOptions({
      pageSize: 'all',
      includeSchoolClass: true,
      includeAllSchoolClasses: true,
    }),
    enabled: open,
  })

  /**
   * Derived rather than synced through an effect: `groupKey` only ever holds a user's choice.
   *
   * With no choice made the default is the soonest block that still has a seat to spend, NOT simply
   * the soonest one. Seats are handed out soonest-first, so the soonest block is the first to run
   * dry — defaulting to it would open the dialog on "Kursi tersedia: 0" and refuse every selection
   * while the school's other blocks sat full.
   */
  const selectedGroup =
    groups.find((group) => group.key === groupKey) ??
    // A LIVE block with seats first. A scheduled one is a deliberate choice — opening the dialog on
    // it would have an admin hand out seats that do nothing until next term without meaning to —
    // so it is only the default when nothing usable today has a seat left. An add-on that has
    // already started is live like any other block and defaults on the same terms.
    groups.find((group) => !group.isScheduled && group.seatsAvailable > 0) ??
    groups.find((group) => !group.isScheduled) ??
    groups.find((group) => group.seatsAvailable > 0) ??
    groups[0]
  const seatsAvailable = selectedGroup?.seatsAvailable ?? 0

  /**
   * The picker's three sections. An empty one is not rendered at all — a labelled heading with
   * nothing under it reads as a block the school has and cannot use.
   */
  const groupSections = useMemo(
    () =>
      [
        { label: 'Periode saat ini', items: groups.filter((g) => g.kind !== 'add-on' && !g.isScheduled) },
        { label: 'Periode yang akan datang', items: groups.filter((g) => g.kind !== 'add-on' && g.isScheduled) },
        // Every add-on purchase, live or future. Each row is one top-up spanning every term it was
        // bought against, so it belongs under neither period heading.
        { label: 'Kursi tambahan', items: groups.filter((g) => g.kind === 'add-on') },
      ].filter((section) => section.items.length > 0),
    [groups],
  )

  const allChildren = useMemo(() => childrenData?.data ?? [], [childrenData])
  // `heldSeatId`, not the two badge fields: it is the loose "holds any live seat anywhere" test the
  // server's own guard applies, so the picker offers exactly the children a handout would accept. The
  // badge fields are bounded to the seat covering TODAY, which would let a child holding only a seat
  // prepaid for next term into this list — and the write would then be refused (single handout) or
  // silently skip them (bulk), reporting fewer seats given than were selected.
  /**
   * Who can take a seat from the BLOCK CURRENTLY SELECTED.
   *
   * For a live block this is `heldSeatId === null` — the loose "holds any live seat anywhere" test
   * the server's own guard applies, so the picker offers exactly the children a handout accepts. An
   * add-on that has already started is live, and asks the same question.
   *
   * For a SCHEDULED block the question is different, and asking the live one would empty the list:
   * the students a school wants on next term's seats are precisely the ones sitting on this term's.
   * What disqualifies them there is cover that reaches INTO the new window — a seat already granted
   * for next term, or a current seat running past the day the new block opens.
   */
  const eligible = useMemo(() => {
    if (!selectedGroup?.isScheduled) return allChildren.filter((child) => child.heldSeatId === null)

    const poolStart = new Date(selectedGroup.startDate).getTime()

    return allChildren.filter((child) => {
      if (child.premiumScheduledEndDate !== null) return false
      if (child.premiumEndDate === null) return true

      return new Date(child.premiumEndDate).getTime() <= poolStart
    })
  }, [allChildren, selectedGroup])
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return eligible.filter((child) => {
      const matchClass =
        schoolClassId === null
          ? true
          : schoolClassId === NO_SCHOOL_CLASS_ID
            ? (child.schoolClasses?.length ?? 0) === 0
            : (child.schoolClasses?.some((schoolClass) => schoolClass.id === schoolClassId) ?? false)
      const matchSearch = term === '' || child.name.toLowerCase().includes(term)
      return matchClass && matchSearch
    })
  }, [eligible, schoolClassId, search])

  const isLoading = isPendingPools || isPendingChildren
  // Checked before the empty states: a failed fetch leaves `isPending` false and `data` undefined,
  // so "we could not read the roster" would otherwise render as "this school has no students" and
  // send an admin off to enrol children who are already there.
  const isError = isErrorPools || isErrorChildren
  const stateNoStudents = !isLoading && !isError && allChildren.length === 0
  const stateAllPremium = !isLoading && !isError && allChildren.length > 0 && eligible.length === 0
  const isOverAllocated = selectedIds.length > seatsAvailable

  const mutation = useMutation(
    trpc.myOrganization.giveChildrenPremium.mutationOptions({
      onSuccess: async (result) => {
        toast.success(`${result.count} murid berhasil diberi akses premium`)
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: trpc.myOrganization.listChildren.queryKey() }),
          queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getSeatPools.queryKey() }),
          // Scheduled pools are a separate query key; a seat handed out from a future term lives
          // only there, and the header totals read it.
          queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getSeatPoolsWithScheduled.queryKey() }),
          // This dialog's own read — the same seats cut by window rather than by pool.
          queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getSeatGroupsWithScheduled.queryKey() }),
          queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getSeatSummary.queryKey() }),
          queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getActiveSeats.queryKey() }),
        ])
        close()
      },
      onError: (error) => {
        toast.error(error.message || 'Gagal memberikan akses')
      },
    }),
  )

  const reset = () => {
    setSelectedIds([])
    setGroupKey(null)
    setSchoolClassId(null)
    setSearch('')
  }

  const close = () => {
    reset()
    onOpenChange(false)
  }

  /**
   * Buying more seats leaves the dialog, so the current selection is dropped with it — the seats
   * bought here land in a new pool and the picker would have to start over anyway. The shortfall is
   * carried over as the quantity to buy.
   */
  const goToAddOn = () => {
    const shortfall = Math.max(0, selectedIds.length - seatsAvailable)
    close()
    onBuySeats?.(shortfall)
  }

  /**
   * Whether "Tambah kursi" applies to the pool on screen.
   *
   * An add-on joins the blocks the school PAYS for — a grant (beasiswa, pilot, pemda, internal) is
   * sized by whoever sponsored it and cannot be topped up here, so offering a plus beside it sends
   * the admin to a checkout that cannot change the number they were looking at.
   *
   * True while no pool is resolved yet, so the button does not flicker out during the first fetch.
   */
  const canBuyMoreSeats = Boolean(onBuySeats) && (!selectedGroup || selectedGroup.isCommercial)

  const canSubmit = selectedIds.length > 0 && !isOverAllocated && !mutation.isPending

  const noun = type === 'web' ? 'anak' : 'murid'

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent className='srd:text-neutral-black srd:flex srd:h-fit srd:max-h-[95dvh] srd:flex-col srd:overflow-y-auto srd:sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>Kelola kursi</DialogTitle>
          <DialogDescription>Gunakan kursi untuk memberi {noun} akses premium.</DialogDescription>
        </DialogHeader>

        <section className='srd:flex srd:flex-wrap srd:items-end srd:gap-x-6 srd:gap-y-3'>
          {/* Nothing to choose with a single block, so the picker is hidden and that block is spent. */}
          {groups.length > 1 && (
            <div className='srd:flex srd:min-w-[200px] srd:flex-1 srd:flex-col srd:gap-y-1'>
              <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>Pilih periode</p>
              <Select value={selectedGroup?.key ?? ''} onValueChange={setGroupKey} disabled={mutation.isPending}>
                <SelectTrigger size='sm' className='srd:w-full'>
                  {/* The window rides along in the collapsed trigger, not only in the open list:
                      `groupLabel` names the entitlement flavour, so a school holding two purchased
                      pools would otherwise read "Kursi dibeli" twice and have no way to tell which
                      one it is about to spend. */}
                  <SelectValue>
                    {selectedGroup
                      ? `${groupLabel(selectedGroup)} · ${groupWindowLabel(selectedGroup)}`
                      : 'Pilih periode'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {groupSections.map((section) => (
                    <SelectGroup key={section.label}>
                      <SelectLabel>{section.label}</SelectLabel>

                      {section.items.map((group) => (
                        <SelectItem key={group.key} value={group.key} className='srd:py-1.5'>
                          <span className='srd:flex srd:flex-col'>
                            <span className={typographyVariants({ variant: 'label-md' })}>{groupLabel(group)}</span>
                            <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
                              {groupWindowLabel(group)}
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className='srd:flex srd:flex-col srd:gap-y-1'>
            <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>
              {selectedGroup?.isScheduled ? 'Kursi periode berikutnya' : 'Kursi tersedia'}
            </p>
            <div className='srd:flex srd:items-center srd:gap-x-2'>
              {isPendingPools ? (
                <Skeleton className='srd:h-6 srd:w-8' />
              ) : (
                <p className={typographyVariants({ variant: 'title-sm' })}>{seatsAvailable}</p>
              )}
              {canBuyMoreSeats && (
                <Button
                  variant='ghost'
                  size='icon'
                  className='srd:size-8'
                  aria-label='Tambah kursi'
                  disabled={mutation.isPending}
                  onClick={goToAddOn}
                >
                  <CirclePlus className='srd:size-5 srd:text-blue-100' />
                </Button>
              )}
            </div>
          </div>
        </section>

        {/* Said plainly, because the result is invisible until that date: a student assigned here
            stays Terbatas today and turns Premium when the pool opens. Without this the admin
            assigns seats, sees nothing change on the roster, and assigns again. */}
        {selectedGroup?.isScheduled && (
          <Alert variant='neutral'>
            <AlertDescription>
              Kursi ini baru aktif {formatDate(selectedGroup.startDate, { dateStyle: 'long' })}. Murid yang dipilih akan
              jadi Premium mulai tanggal tersebut.
            </AlertDescription>
          </Alert>
        )}

        {isError ? (
          <section className='srd:flex srd:flex-col srd:items-center srd:gap-y-4 srd:py-10 srd:text-center'>
            <p
              className={cn(
                typographyVariants({ variant: 'body-md' }),
                'srd:text-error srd:flex srd:items-center srd:gap-x-2',
              )}
            >
              <X className='srd:size-4' />
              Gagal mengambil data kursi
            </p>
            <Button
              size='sm'
              variant='outline'
              className='srd:gap-x-2'
              onClick={() => {
                if (isErrorPools) void refetchPools()
                if (isErrorChildren) void refetchChildren()
              }}
            >
              <RefreshCw className='srd:size-3.5' />
              Coba lagi
            </Button>
          </section>
        ) : stateNoStudents ? (
          <section className='srd:flex srd:flex-col srd:items-center srd:gap-y-1 srd:py-10 srd:text-center'>
            <p className={typographyVariants({ variant: 'title-sm' })}>Belum ada {noun} terdaftar</p>
            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>
              Mulai daftarkan {noun}-{noun}mu
            </p>
          </section>
        ) : stateAllPremium ? (
          <section className='srd:flex srd:flex-col srd:items-center srd:gap-y-1 srd:py-10 srd:text-center'>
            <p className={typographyVariants({ variant: 'title-sm' })}>Semua murid sudah memiliki akses premium 🎉</p>
            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>
              Tidak ada yang perlu diberi akses saat ini.
            </p>
          </section>
        ) : (
          <section className='srd:flex srd:flex-col srd:gap-y-2'>
            <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>Pilih murid</p>

            <div className='srd:flex srd:w-full srd:flex-wrap srd:items-center srd:gap-2'>
              <SchoolClassSelect
                value={schoolClassId}
                onValueChange={setSchoolClassId}
                size='sm'
                className='srd:w-full srd:min-w-[200px] srd:flex-1'
                showNoClass
                disabled={mutation.isPending}
              />

              <InputGroup className='srd:w-full srd:min-w-[200px] srd:flex-1' size='sm'>
                <InputGroupAddon align='inline-start'>
                  <Search className='srd:size-4 srd:text-blue-100' />
                </InputGroupAddon>
                <InputGroupInput
                  type='search'
                  placeholder='Cari'
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  disabled={mutation.isPending}
                />
              </InputGroup>
            </div>

            {isOverAllocated && (
              <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-error')}>
                Melebihi kursi tersedia
              </p>
            )}

            {isLoading ? (
              <Skeleton className='srd:h-[240px] srd:w-full' />
            ) : (
              <StudentPickerList
                students={filtered}
                selected={selectedIds}
                onSelectedChange={setSelectedIds}
                disabled={mutation.isPending}
                emptyLabel={type === 'web' ? 'Anak tidak ditemukan' : 'Murid tidak ditemukan'}
              />
            )}
          </section>
        )}

        <DialogFooter>
          <Button variant='outline' disabled={mutation.isPending} onClick={close}>
            Kembali
          </Button>

          <Button
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={() =>
              mutation.mutate({
                userIds: selectedIds,
                // Always names the block the picker showed, single-block schools included, so the
                // seats spent are the ones the admin was looking at. Exactly one of the two is set:
                // an add-on is scoped by its own id, because its seats and its pool's are separate
                // windows that the server would otherwise spend interchangeably.
                membershipId: selectedGroup?.kind === 'add-on' ? undefined : selectedGroup?.membershipId,
                addOnInvoiceIds:
                  selectedGroup?.addOnInvoiceIds && selectedGroup.addOnInvoiceIds.length > 0
                    ? selectedGroup.addOnInvoiceIds
                    : undefined,
              })
            }
          >
            Berikan akses
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
